import { ChangeDetectionStrategy, Component, ElementRef, viewChild } from '@angular/core';
import { MngBaseButtonComponent } from './mng-base-button.component';
import { LayoutAlignDirective, LayoutDirective } from '@ngbracket/ngx-layout/flex';
import { MngIconComponent } from '@mng/mng-icon';
import { MatButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { MatMenu, MatMenuContent, MatMenuTrigger } from '@angular/material/menu';
import { MngMenuButtonComponent } from './mng-menu-button.component';
import { NgTemplateOutlet } from '@angular/common';

@Component({
  selector: 'mng-button',
  imports: [
    LayoutDirective,
    LayoutAlignDirective,
    MngIconComponent,
    MatButton,
    MatTooltip,
    MatMenuTrigger,
    MatMenu,
    MatMenuContent,
    MngMenuButtonComponent,
    NgTemplateOutlet
  ],
  template: `
    <ng-template #tplButtonContent let-actionDef>
      <span style="pointer-events: none;" fxLayout="row" fxLayoutAlign="center center">
        @if (actionDef.iconTemplate) {
          <span style="margin-right: 6px;" [innerHTML]="actionDef.iconTemplate"></span>
        }
        @if (iconName) {
          <mng-icon style="margin-right: 6px" [color]="iconColor" [iconName]="iconName" [fontSize]="20" />
        }
        <span style="height: 34px;" fxLayout="row" fxLayoutAlign="start center">
          <span [innerHTML]="sanitizeHtml(text)"></span>
          @if (actionDef.externalUrlTarget === '_blank') {
            <mng-icon style="margin-left: 4px" iconName="open-in-new" [inline]="true" [fontSize]="14" />
          }
          @if (actionDef.menu) {
            <mng-icon style="margin-left: 2px" iconName="menu-down" [inline]="true" [fontSize]="20" />
          }
        </span>
      </span>
    </ng-template>

    @if (isVisible) {
      <div
        [matTooltip]="isDisabled ? getDisabledTooltipMessage() : getTooltipMessage()"
        [matTooltipPosition]="tooltipPosition"
        [matTooltipShowDelay]="isDisabled ? 50 : tooltipDelay"
      >
        @if (!actionDef.menu && !actionDef.menuRef) {
          @if (actionDef.variant === 'basic') {
            <button
              #mngButton
              mat-button
              class="mng-button"
              [id]="getButtonId()"
              [disabled]="isDisabled"
              [style.width]="sanitizeStyle(actionDef.width)"
              [color]="buttonColor"
              [attr.type]="actionDef?.additionalProps?.type || 'submit'"
              (click)="executeAction($event)"
            >
              <ng-container *ngTemplateOutlet="tplButtonContent; context: { $implicit: actionDef }" />
            </button>
          }
          @if (actionDef.variant === 'raised') {
            <button
              #mngButton
              mat-raised-button
              class="mng-button"
              [id]="getButtonId()"
              [disabled]="isDisabled"
              [style.width]="sanitizeStyle(actionDef.width)"
              [color]="buttonColor"
              (click)="executeAction($event)"
            >
              <ng-container *ngTemplateOutlet="tplButtonContent; context: { $implicit: actionDef }" />
            </button>
          }
          @if (actionDef.variant === 'flat') {
            <button
              #mngButton
              mat-flat-button
              class="mng-button"
              [id]="getButtonId()"
              [disabled]="isDisabled"
              [style.width]="sanitizeStyle(actionDef.width)"
              [color]="buttonColor"
              (click)="executeAction($event)"
            >
              <ng-container *ngTemplateOutlet="tplButtonContent; context: { $implicit: actionDef }" />
            </button>
          }
          @if (actionDef.variant === 'stroked') {
            <button
              #mngButton
              mat-stroked-button
              class="mng-button"
              [id]="getButtonId()"
              [disabled]="isDisabled"
              [style.width]="sanitizeStyle(actionDef.width)"
              [color]="buttonColor"
              (click)="executeAction($event)"
            >
              <ng-container *ngTemplateOutlet="tplButtonContent; context: { $implicit: actionDef }" />
            </button>
          }
        } @else {
          @if (actionDef.variant === 'basic') {
            <button
              #mngButton
              mat-button
              class="mng-button"
              [matMenuTriggerFor]="actionDef.menu ? buttonMenu : actionDef.menuRef"
              [matMenuTriggerData]="{ actionArg: actionArg }"
              [id]="getButtonId()"
              [disabled]="isDisabled"
              [style.width]="sanitizeStyle(actionDef.width)"
              [color]="buttonColor"
              (click)="executeAction($event)"
            >
              <ng-container *ngTemplateOutlet="tplButtonContent; context: { $implicit: actionDef }" />
            </button>
          }
          @if (actionDef.variant === 'raised') {
            <button
              #mngButton
              mat-raised-button
              class="mng-button"
              [id]="getButtonId()"
              [matMenuTriggerFor]="actionDef.menu ? buttonMenu : actionDef.menuRef"
              [matMenuTriggerData]="{ actionArg: actionArg }"
              [disabled]="isDisabled"
              [style.width]="sanitizeStyle(actionDef.width)"
              [color]="buttonColor"
              (click)="executeAction($event)"
            >
              <ng-container *ngTemplateOutlet="tplButtonContent; context: { $implicit: actionDef }" />
            </button>
          }
          @if (actionDef.variant === 'flat') {
            <button
              #mngButton
              mat-flat-button
              class="mng-button"
              [id]="getButtonId()"
              [matMenuTriggerFor]="actionDef.menu ? buttonMenu : actionDef.menuRef"
              [matMenuTriggerData]="{ actionArg: actionArg }"
              [disabled]="isDisabled"
              [style.width]="sanitizeStyle(actionDef.width)"
              [color]="buttonColor"
              (click)="executeAction($event)"
            >
              <ng-container *ngTemplateOutlet="tplButtonContent; context: { $implicit: actionDef }" />
            </button>
          }
          @if (actionDef.variant === 'stroked') {
            <button
              #mngButton
              mat-stroked-button
              class="mng-button"
              [id]="getButtonId()"
              [matMenuTriggerFor]="actionDef.menu ? buttonMenu : actionDef.menuRef"
              [matMenuTriggerData]="{ actionArg: actionArg }"
              [disabled]="isDisabled"
              [style.width]="sanitizeStyle(actionDef.width)"
              [color]="buttonColor"
              (click)="executeAction($event)"
            >
              <ng-container *ngTemplateOutlet="tplButtonContent; context: { $implicit: actionDef }" />
            </button>
          }
          <mat-menu #buttonMenu="matMenu">
            <ng-template matMenuContent>
              @for (menuActionDef of actionDef.menu; track menuActionDef; let i = $index) {
                <mng-menu-button
                  [actionDef]="menuActionDef"
                  [actionArg]="actionArg"
                  (executeMenuAction)="executeMenuAction($event, i)"
                />
              }
            </ng-template>
          </mat-menu>
        }
      </div>
    }
  `,
  styles: [
    `
      :host {
        display: inline-block;
      }
    `
  ],
  changeDetection: ChangeDetectionStrategy.Eager
})
export class MngButtonComponent extends MngBaseButtonComponent {
  readonly button = viewChild.required<ElementRef<HTMLButtonElement>>('mngButton');

  focus(): void {
    this.button().nativeElement.focus();
  }
}
